import type { QueryClient } from '@tanstack/react-query'
import { ChecklistenLoginForm } from '@/components/auth/checklisten-login-form'
import { getMeOptions } from '@/client/@tanstack/react-query.gen'
import { createFileRoute, isRedirect, redirect } from '@tanstack/react-router'

function getErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status
    return typeof s === 'number' ? s : undefined
  }
  return undefined
}

function toRoutePath(redirect: string | undefined, basepath: string) {
  if (!redirect) return '/'
  const b = basepath.replace(/\/$/, '')
  if (redirect === b || redirect === `${b}/`) return '/'
  if (redirect.startsWith(`${b}/`)) {
    const rest = redirect.slice(b.length)
    return rest.length > 0 ? rest : '/'
  }
  return redirect
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const queryClient = context.queryClient
    try {
      const user = await queryClient.ensureQueryData({
        ...getMeOptions(),
      } as Parameters<QueryClient['ensureQueryData']>[0])
      if (user) {
        throw redirect({
          to: toRoutePath(search.redirect, '/checklisten'),
          replace: true,
        })
      }
    } catch (e) {
      if (isRedirect(e)) throw e
      const status = getErrorStatus(e)
      if (status === 401 || status === 403) return
      // API unreachable — still show login instead of a blank error surface
      if (status == null) return
      throw e
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { redirect: redirectAfterLogin } = Route.useSearch()

  return (
    <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-3xl">
        <ChecklistenLoginForm redirectAfterLogin={redirectAfterLogin} />
      </div>
    </div>
  )
}
